import { OpenInNew } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/AddOutlined';
import { Alert, Box, Button, Card, Checkbox, FormControlLabel, Typography } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import styles from 'client/modules/Settings/pages/IntegrationsPage/IntegrationsListPage.css';
import { getSurveys } from 'client/modules/Surveys/SurveyReducer';
import useOpenClose from 'client/util/useOpenClose';
import React from 'react';
import { IoTrashOutline as DeleteIcon, IoPencil as EditIcon } from 'react-icons/io5';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useOutletContext } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import Config from '../../../../../../server/config';
import TriggersModal from './TriggersModal';

const messages = defineMessages({
  trigger: {
    id: 'IntegrationConfigSalesforce.Trigger',
    defaultMessage: 'Trigger',
  },
  delay: {
    id: 'IntegrationConfigSalesforce.Delay',
    defaultMessage: 'Send survey after',
  },
  createdCase: {
    id: 'IntegrationConfigSalesforce.CreatedCase',
    defaultMessage: 'Case created',
  },
  closedCase: {
    id: 'IntegrationConfigSalesforce.ClosedCase',
    defaultMessage: 'Case closed',
  },
  createdOpportunity: {
    id: 'IntegrationConfigSalesforce.CreatedOpportunity',
    defaultMessage: 'Opportunity created',
  },
  wonOpportunity: {
    id: 'IntegrationConfigSalesforce.WonOpportunity',
    defaultMessage: 'Opportunity won',
  },
  lostOpportunity: {
    id: 'IntegrationConfigSalesforce.LostOpportunity',
    defaultMessage: 'Opportunity lost',
  },
  Custom: {
    id: 'IntegrationConfigSalesforce.Custom',
    defaultMessage: 'Custom',
  },
  Immediately: {
    id: 'IntegrationConfigSalesforce.Immediately',
    defaultMessage: 'Immediately',
  },
  '1 Hour': {
    id: 'IntegrationConfigSalesforce.1Hour',
    defaultMessage: '1 Hour',
  },
  '1 Day': {
    id: 'IntegrationConfigSalesforce.1Day',
    defaultMessage: '1 Day',
  },
  '1 Week': {
    id: 'IntegrationConfigSalesforce.1Week',
    defaultMessage: '1 Week',
  },
  noTriggers: {
    id: 'IntegrationConfigSalesforce.NoTriggers',
    defaultMessage: 'No triggers',
  },
  outboundMessageUrl: {
    id: 'IntegrationConfigSalesforce.OutboundMessageUrl',
    defaultMessage: 'The target URL for your Salesforce outbound message.',
  },
  syncResponses: {
    id: 'IntegrationsSettings.SalesforceSyncResponses',
    defaultMessage: 'Sync responses to contacts',
  },
  syncResponsesDescription: {
    id: 'IntegrationsSettings.SalesforceSyncResponsesDescription',
    defaultMessage:
      'Every time a response is created, we will update the Salesforce contact matching the response email with the latest survey data.',
  },
  addSyncSurvey: {
    id: 'IntegrationsSettings.SalesforceAddSyncSurvey',
    defaultMessage: 'Add survey to sync',
  },
  survey: {
    id: 'IntegrationsSettings.SalesforceSurvey',
    defaultMessage: 'Survey',
  },
  surveys: {
    id: 'IntegrationsSettings.SalesforceSurveys',
    defaultMessage: 'Surveys',
  },
  remove: {
    id: 'IntegrationsSettings.Remove',
    defaultMessage: 'Remove',
  },
  noSurveys: {
    id: 'IntegrationsSettings.noSurveys',
    defaultMessage: 'No surveys to sync',
  },
  triggersSection: {
    id: 'IntegrationsSettings.SalesforceTriggersSection',
    defaultMessage: 'Triggers',
  },
  addTrigger: {
    id: 'IntegrationsSettings.SalesforceAddTrigger',
    defaultMessage: 'Add Trigger',
  },
  edit: {
    id: 'IntegrationsSettings.Edit',
    defaultMessage: 'Edit',
  },
  survey: {
    id: 'IntegrationsSettings.SalesforceSurvey',
    defaultMessage: 'Survey',
  },
  CustomTriggerDescription: {
    id: 'IntegrationConfigSalesforce.CustomTriggerDescription',
    defaultMessage:
      'Establish personalized triggers in Salesforce for specific entity categories such as Account, Case, Contact, and Opportunity by following the guidelines outlined in our documentation.',
  },
  cancel: {
    id: 'Confirm.Cancel',
    defaultMessage: 'Cancel',
  },
  save: {
    id: 'Confirm.Save',
    defaultMessage: 'Save',
  },
  TriggersTitle: {
    id: 'IntegrationConfigSalesforce.TriggersTitle',
    defaultMessage: 'Send surveys using Salesforce Triggers',
  },
  Documentation: {
    id: 'IntegrationConfigSalesforce.Documentation',
    defaultMessage: 'Learn more',
  },
  EnableTriggers: {
    id: 'IntegrationConfigSalesforce.EnableTriggers',
    defaultMessage: 'Enable Salesforce Triggers',
  },
});

const objectEvents = {
  Case: ['created', 'closed'],
  Opportunity: ['created', 'won', 'lost'],
  Custom: [''],
};
const triggerTypes = Object.keys(objectEvents)
  .map(object =>
    objectEvents[object].map(event => ({
      value: [object, event].filter(v => v).join(' '),
      label: `${event}${object}`,
    })),
  )
  .flat();
const delayTypes = ['Immediately', '1 Hour', '1 Day', '1 Week'];

const IntegrationConfigSalesforce = () => {
  const { formatMessage } = useIntl();
  const { integration, onIntegrationsPropertiesChange } = useOutletContext();
  const {
    instanceUrl,
    notificationToken,
    syncResponses = false,
    syncSurveys = [],
    triggers: integrationTriggers = [],
    triggersEnabled = true,
  } = integration || {};
  const [searchParams] = useSearchParams();
  const installPackage = searchParams.get('installPackage');
  const surveys = useSelector(getSurveys);
  const surveyOptions = surveys.map(s => ({ value: s.id, name: s.name || s.brand }));
  const [triggers, setTriggers] = React.useState([]);
  const [isTriggersModalOpen, openTriggersModal, closeTriggersModal, { triggerIdx }] =
    useOpenClose();
  const [host] = instanceUrl && new URL(instanceUrl).host.split('.');

  React.useEffect(() => {
    // Initialize triggers from integration on mount or integration change
    setTriggers(Array.isArray(integrationTriggers) ? integrationTriggers : []);
  }, [integrationTriggers]);

  // Handler for toggling syncResponses
  const onToggleSyncResponses = event => {
    onIntegrationsPropertiesChange(event, 'salesforce', { syncResponses: event.target.checked });
    if (event.target.checked && !syncSurveys?.length && surveys.length) {
      onIntegrationsPropertiesChange(null, 'salesforce', {
        syncSurveys: [surveys[0].id],
      });
    }
  };

  // Handler for saving triggers from modal
  const handleTriggersSave = (updatedTrigger, idx) => {
    let newTriggers;
    if (typeof idx === 'number') {
      newTriggers = triggers.map((t, i) => (i === idx ? updatedTrigger : t));
    } else {
      newTriggers = [...triggers, updatedTrigger];
    }
    setTriggers(newTriggers);
    onIntegrationsPropertiesChange(null, 'salesforce', { triggers: newTriggers });
  };

  // Handler for deleting a trigger
  const handleDeleteTrigger = idx => {
    const newTriggers = triggers.filter((_, i) => i !== idx);
    setTriggers(newTriggers);
    onIntegrationsPropertiesChange(null, 'salesforce', { triggers: newTriggers });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography sx={{ gap: 1, display: 'inline-flex' }}>
        Site:
        <a href={instanceUrl} target="_blank" rel="noreferrer">
          {host}
          <OpenInNew sx={{ fontSize: 16, verticalAlign: 'middle', ml: 0.5 }} />
        </a>
      </Typography>

      {!!installPackage && (
        <Alert severity="warning" sx={{ width: '100%' }}>
          <FormattedMessage
            id="IntegrationConfigSalesforce.PackageNotInstalled"
            defaultMessage="Promoter Ninja package must be installed in Salesforce to enable this integration. {link}."
            values={{
              link: (
                <a href={installPackage} target="_blank" rel="noreferrer">
                  <FormattedMessage
                    id="IntegrationConfigSalesforce.PackageNotInstalledLink"
                    defaultMessage="Install package"
                  />
                </a>
              ),
            }}
          />
        </Alert>
      )}

      {/* Sync Responses Section */}
      <Box>
        <Box className={styles.syncSurveysDivider} />

        <FormControlLabel
          control={
            <Checkbox checked={syncResponses} onChange={onToggleSyncResponses} color="primary" />
          }
          label={<Box sx={{ fontWeight: 600 }}>{formatMessage(messages.syncResponses)}</Box>}
          className={styles.syncSurveysCheckboxLabel}
        />
        <div className={styles.syncSurveysCheckboxDescription}>
          {formatMessage(messages.syncResponsesDescription)}
        </div>
        {(!syncResponses && !syncSurveys?.length) || (
          <Box
            className={
              styles.syncSurveysSection +
              (!syncResponses ? ` ${styles.syncSurveysSectionDisabled}` : '')
            }
          >
            <Autocomplete
              multiple
              options={surveyOptions}
              getOptionLabel={option => option.name}
              value={surveyOptions.filter(opt => syncSurveys.includes(opt.value))}
              onChange={(_, newValue) => {
                onIntegrationsPropertiesChange(null, 'salesforce', {
                  syncSurveys: newValue.map(opt => opt.value),
                });
              }}
              disableCloseOnSelect
              renderInput={params => (
                <TextField
                  {...params}
                  variant="standard"
                  label={formatMessage(messages.surveys)}
                  fullWidth
                />
              )}
              disabled={!syncResponses}
              sx={{ minWidth: 220, maxWidth: 400, mb: 2 }}
              fullWidth
            />
          </Box>
        )}
        <Box className={styles.syncSurveysDivider} />
      </Box>
      {/* End Sync Responses Section */}

      {/* Triggers Section */}
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={triggersEnabled}
              onChange={e =>
                onIntegrationsPropertiesChange(null, 'salesforce', {
                  triggersEnabled: e.target.checked,
                })
              }
              color="primary"
            />
          }
          label={<Box sx={{ fontWeight: 600 }}>{formatMessage(messages.TriggersTitle)}</Box>}
          className={styles.syncSurveysCheckboxLabel}
        />
        <div className={styles.syncSurveysCheckboxDescription}>
          <FormattedMessage
            id="IntegrationConfigSalesforce.TriggersDescription"
            defaultMessage="You can automatically send surveys when cases are created/closed, opportunities are won/lost, or create custom triggers. {documentationLink}"
            values={{
              documentationLink: (
                <a
                  href="https://www.promoter.ninja/help/integrations/salesforce-integration"
                  target="_blank"
                  rel="noreferrer"
                >
                  {formatMessage(messages.Documentation)}
                </a>
              ),
            }}
          />
        </div>
        {(!triggersEnabled && triggers.length === 0) || (
          <Box
            className={[
              styles.syncSurveysSection,
              !triggersEnabled && styles.syncSurveysSectionDisabled,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <Box className={styles.syncSurveysAddBtn}>
              <Button
                variant="text"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => openTriggersModal({ triggerIdx: undefined })}
                className={styles.syncSurveysAddBtnInner}
              >
                {formatMessage(messages.addTrigger)}
              </Button>
            </Box>
            <Card variant="outlined" className={styles.syncSurveysTable}>
              <Box className={styles.syncSurveysHeader}>
                <Box className={styles.syncSurveysHeaderSurvey}>
                  {formatMessage(messages.survey)}
                </Box>
                <Box className={styles.syncSurveysHeaderTrigger}>
                  {formatMessage(messages.trigger)}
                </Box>
                <Box className={styles.syncSurveysHeaderDelay}>{formatMessage(messages.delay)}</Box>
                <Box className={styles.syncSurveysHeaderActions} />
              </Box>
              {triggers.length === 0 ? (
                <Box className={styles.syncSurveysEmpty}>{formatMessage(messages.noTriggers)}</Box>
              ) : (
                triggers.map((trigger, idx) => {
                  const survey = surveyOptions.find(s => s.value === trigger.survey);
                  const triggerLabel = triggerTypes.find(t => t.value === trigger.trigger)?.label;
                  const delayLabel = delayTypes.includes(trigger.delay)
                    ? formatMessage(messages[trigger.delay] || { defaultMessage: trigger.delay })
                    : trigger.delay;
                  return (
                    <Box key={trigger._id || idx} className={styles.syncSurveysRow}>
                      <Box className={styles.syncSurveysRowSurvey}>{survey ? survey.name : ''}</Box>
                      <Box className={styles.syncSurveysRowTrigger}>
                        {messages[triggerLabel]
                          ? formatMessage(messages[triggerLabel])
                          : triggerLabel}
                      </Box>
                      <Box className={styles.syncSurveysRowDelay}>{delayLabel}</Box>
                      <Box className={styles.syncSurveysRowActions}>
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            openTriggersModal({ triggerIdx: idx });
                          }}
                          aria-label={formatMessage(messages.edit)}
                          title={formatMessage(messages.edit)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            handleDeleteTrigger(idx);
                          }}
                          aria-label={formatMessage(messages.remove)}
                          title={formatMessage(messages.remove)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Card>
          </Box>
        )}
      </Box>

      <TriggersModal
        open={isTriggersModalOpen}
        onClose={closeTriggersModal}
        triggerIdx={triggerIdx}
        onSave={handleTriggersSave}
        triggers={triggers}
        surveyOptions={surveyOptions}
        triggerTypes={triggerTypes}
        delayTypes={delayTypes}
        messages={messages}
        notificationToken={notificationToken}
        configRootUrl={Config.rootUrl}
      />
      {/* End Triggers Section */}
      <Box className={styles.syncSurveysDivider} />
    </Box>
  );
};

export default IntegrationConfigSalesforce;
